import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  CircularProgress,
  useTheme,
  useMediaQuery,
  IconButton,
  Modal
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import TextField from '@mui/material/TextField';
import DownloadIcon from '@mui/icons-material/Download';
import EmailIcon from '@mui/icons-material/Email';
import HistoryIcon from '@mui/icons-material/History';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from 'notistack';
import enLocale from 'date-fns/locale/en-GB';
import { fetchHistoricalTrades } from '../../services/backendAPI';
import { format } from 'date-fns';

const ExportTradeHistoryModal = ({ 
  open, 
  onClose, 
  defaultExchange = 'NFO',
  title = 'Export Trade History',
  showViewDetailsButton = false,
  onViewDetails = () => {}
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  
  // Get date 5 days before the given date
  const getFiveDaysBefore = () => {
    const today = new Date();
    const fiveDaysBefore = new Date(today);
    fiveDaysBefore.setDate(today.getDate() - 5);
    return fiveDaysBefore;
  };
  
  // Set initial states with proper defaults - accounting for weekends and holidays
  const today = new Date();
  const [endDate, setEndDate] = useState(today);
  const [startDate, setStartDate] = useState(getFiveDaysBefore());
  const [segment, setSegment] = useState(defaultExchange);
  
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  
  useEffect(() => {
    if (open) {
      // Initialize dates when modal opens - current date and 5 days ago
      const today = new Date();
      const fiveDaysAgo = getFiveDaysBefore();
      setEndDate(today);
      setStartDate(fiveDaysAgo);
      setSegment(defaultExchange);
    }
  }, [open, defaultExchange]);
  
  // Update startDate when endDate changes to maintain 3-month relationship
  useEffect(() => {
    if (endDate) {
      // Calculate the date that is 3 months before the selected end date
      const calculatedStartDate = getFiveDaysBefore(endDate);
      
      // Only auto-update if the user hasn't manually changed the start date
      // or if the current start date is outside the valid range
      if (!startDate || startDate > endDate) {
        setStartDate(calculatedStartDate);
      }
    }
  }, [endDate]);
  
  const handleSegmentChange = (event) => {
    setSegment(event.target.value);
  };
  
  const isDateRangeValid = () => {
    return startDate && endDate && startDate <= endDate;
  };
  
  const formatDate = (date) => {
    if (!date) return '';
    return format(date, 'yyyy-MM-dd');
  };
  
  const handleDownloadCSV = async () => {
    if (!isDateRangeValid()) {
      enqueueSnackbar('Please select a valid date range', { 
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        autoHideDuration: 1000
      });
      return;
    }
    
    setDownloadLoading(true);
    
    try {
      const payload = {
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),
        exchange: segment,
        info: 'download'
      };
      
      const response = await fetchHistoricalTrades(payload);
      console.log('Download response type:', typeof response);
      
      // The response should now be a blob directly from primusAPI
      if (response) {
        // Create URL from the blob response
        const url = window.URL.createObjectURL(response);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `trade_history_${payload.exchange}_${payload.startDate}_to_${payload.endDate}.csv`);
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        enqueueSnackbar('Trade history downloaded successfully', { 
          variant: 'success',
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
          autoHideDuration: 1000
        });
        onClose();
      } else {
        throw new Error('Failed to download trade history');
      }
    } catch (error) {
      console.error('Download error:', error);
      enqueueSnackbar(error.message || 'Failed to download trade history', { 
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        autoHideDuration: 1000
      });
    } finally {
      setDownloadLoading(false);
    }
  };
  
  const handleSendEmail = async () => {
    if (!isDateRangeValid()) {
      enqueueSnackbar('Please select a valid date range', { 
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        autoHideDuration: 1000
      });
      return;
    }
    
    setEmailLoading(true);
    
    try {
      const payload = {
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),
        exchange: segment,
        info: 'email'
      };
      
      const response = await fetchHistoricalTrades(payload);
      console.log('Email response:', response);
      
      // Email requests will get a JSON response
      if (response && response.status === 'success') {
        enqueueSnackbar('Trade history request sent successfully. You will receive an email shortly.', { 
          variant: 'success',
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
          autoHideDuration: 1000
        });
        onClose();
      } else {
        const errorMsg = response && response.message ? response.message : 'Failed to send trade history request';
        throw new Error(errorMsg);
      }
    } catch (error) {
      enqueueSnackbar(error.message || 'Failed to send trade history request', { 
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        autoHideDuration: 1000
      });
    } finally {
      setEmailLoading(false);
    }
  };
  
  const handleModalClose = () => {
    setDownloadLoading(false);
    setEmailLoading(false);
    onClose();
  };
  
  return (
    <Modal
      open={open}
      onClose={handleModalClose}
      aria-labelledby="trade-history-modal-title"
    >
      <Paper sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: isMobile ? '95%' : 700,
        maxHeight: isMobile ? '90vh' : 'auto',
        overflow: 'auto',
        p: isMobile ? 2 : 4,
        borderRadius: 2,
        outline: 'none',
        boxShadow: 24,
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          mb: 3 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <HistoryIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
            <Typography id="trade-history-modal-title" variant="h6" component="h2" sx={{ fontWeight: 600 }}>
              {title}
            </Typography>
          </Box>
          <IconButton 
            onClick={handleModalClose}
            disabled={downloadLoading || emailLoading}
            sx={{ color: theme.palette.text.secondary }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        
        <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
          Select a date range within the past 3 months and the market segment to export your trade history.
        </Typography>
        
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enLocale}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row', 
            gap: 2, 
            mb: 3 
          }}>
            <DatePicker
              label="From"
              value={startDate}
              minDate={null}
              maxDate={endDate}
              onChange={(newValue) => setStartDate(newValue)}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  fullWidth 
                  InputLabelProps={{
                    style: { fontWeight: 600 }
                  }}
                />
              )}
              inputFormat="dd/MM/yyyy"
            />
            <DatePicker
              label="To"
              value={endDate}
              minDate={startDate}
              maxDate={new Date()}
              onChange={(newValue) => setEndDate(newValue)}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  fullWidth 
                  InputLabelProps={{
                    style: { fontWeight: 600 }
                  }}
                />
              )}
              inputFormat="dd/MM/yyyy"
            />
          </Box>
        </LocalizationProvider>
        
        <Typography variant="body2" sx={{ 
          fontSize: '0.75rem', 
          color: theme.palette.text.secondary,
          mb: 3,
          fontStyle: 'italic' 
        }}>
          {/* Note: Weekends are disabled for selection. For Mondays, the previous Friday is automatically selected. */}
        </Typography>
        
        <FormControl fullWidth sx={{ mb: 3 }} size={isMobile ? "small" : "medium"}>
          <InputLabel id="segment-select-label">Market Segment</InputLabel>
          <Select
            labelId="segment-select-label"
            id="segment-select"
            value={segment}
            label="Market Segment"
            onChange={handleSegmentChange}
          >
            <MenuItem value="NFO">Derivatives</MenuItem>
            <MenuItem value="MCX">Commodities</MenuItem>
            {/* <MenuItem value="NSE">Equity</MenuItem> */}
            {/* <MenuItem value="CDS">Currency</MenuItem> */}
          </Select>
        </FormControl>
        
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'flex-end', 
          gap: 2,
          '& > button': {
            width: isMobile ? '100%' : 'auto'
          }
        }}>
          {showViewDetailsButton && (
            <Button
              onClick={onViewDetails}
              color="primary"
              variant="outlined"
              disabled={downloadLoading || emailLoading}
              startIcon={<HistoryIcon />}
              size={isMobile ? "small" : "medium"}
            >
              View Trade Details
            </Button>
          )}
          <Button 
            variant="contained" 
            color="secondary"
            onClick={handleDownloadCSV}
            disabled={downloadLoading || emailLoading || !isDateRangeValid()}
            startIcon={downloadLoading ? <CircularProgress size={20} /> : <DownloadIcon />}
            size={isMobile ? "small" : "medium"}
          >
            {downloadLoading ? 'Downloading...' : 'Download CSV'}
          </Button>
          {/* <Button 
            variant="contained" 
            onClick={handleSendEmail}
            disabled={downloadLoading || emailLoading || !isDateRangeValid()}
            startIcon={emailLoading ? <CircularProgress size={20} /> : <EmailIcon />}
            size={isMobile ? "small" : "medium"}
          >
            {emailLoading ? 'Sending...' : 'Send to Email'}
          </Button> */}
        </Box>
      </Paper>
    </Modal>
  );
};

export default ExportTradeHistoryModal; 